// Preload Service for Agricverse PWA
// Handles initial data loading and caching for offline-first functionality

import { authAPI } from './api';
import cacheService from './cacheService';
import networkUtils from '../utils/networkUtils';

class PreloadService {
  constructor() {
    this.isPreloading = false;
    this.preloadStatus = {
      total: 0,
      completed: 0,
      failed: 0,
      errors: []
    };
  }

  // Critical API endpoints that should be preloaded for offline use
  getCriticalEndpoints() {
    return [
      // Core produce and farm data
      { method: 'getMyProduce', priority: 1, description: 'My Produce' },
      { method: 'getProduce', priority: 1, description: 'Available Produce' },
      { method: 'getFarmManagement', priority: 1, description: 'Farm Management' },
      { method: 'getCategories', priority: 1, description: 'Categories' },
      
      // Financial data
      { method: 'getFarmerWallet', priority: 2, description: 'Wallet Balance' },
      { method: 'getFarmerOrders', priority: 2, description: 'Orders' },
      { method: 'getFarmerWithdrawals', priority: 3, description: 'Withdrawals' },
      
      // Communication
      { method: 'getConversations', priority: 3, description: 'Conversations' },
      
      // Products for marketplace
      { method: 'getProducts', priority: 4, description: 'Products' }
    ];
  }

  // Start preloading critical data
  async startPreload() {
    if (this.isPreloading) {
      return this.preloadStatus;
    }

    // Check if we have a valid auth token before preloading
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      return { success: false, reason: 'no-auth' };
    }

    this.isPreloading = true;
    
    const endpoints = this.getCriticalEndpoints();
    this.preloadStatus = {
      total: endpoints.length,
      completed: 0,
      failed: 0,
      errors: [],
      startTime: Date.now()
    };

    // Check if we're online
    const isOnline = await networkUtils.isConnected();
    if (!isOnline) {
      this.isPreloading = false;
      return this.preloadStatus;
    }

    // Sort by priority and preload
    const sortedEndpoints = endpoints.sort((a, b) => a.priority - b.priority);
    
    // Preload in batches to avoid overwhelming the server
    const batchSize = 3;
    for (let i = 0; i < sortedEndpoints.length; i += batchSize) {
      const batch = sortedEndpoints.slice(i, i + batchSize);
      
      await Promise.allSettled(
        batch.map(endpoint => this.preloadEndpoint(endpoint))
      );
      
      // Brief pause between batches
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    this.preloadStatus.duration = Date.now() - this.preloadStatus.startTime;
    this.isPreloading = false;

    return this.preloadStatus;
  }

  // Preload a single endpoint
  async preloadEndpoint(endpoint) {
    // Check token again before making request
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      return; // Silently skip if no token
    }
    
    try {
      // Call the API method
      const data = await authAPI[endpoint.method]();
      
      if (data) {
        this.preloadStatus.completed++;
        
        // Notify listeners about progress
        this.notifyProgress(endpoint, true);
      } else {
        throw new Error('No data returned');
      }
    } catch (error) {
      // If 401, token is invalid - stop preloading silently
      if (error.response?.status === 401) {
        this.isPreloading = false;
        return;
      }
      
      this.preloadStatus.failed++;
      this.preloadStatus.errors.push({
        endpoint: endpoint.method,
        description: endpoint.description,
        error: error.message
      });
      
      // Only log non-401 errors
      if (error.response?.status !== 401) {
        console.warn(`[Preload] ❌ Failed to load ${endpoint.description}:`, error.message);
      }
      this.notifyProgress(endpoint, false, error.message);
    }
  }

  // Notify progress to listeners
  notifyProgress(endpoint, success, error = null) {
    const progressEvent = new CustomEvent('preload-progress', {
      detail: {
        endpoint: endpoint.method,
        description: endpoint.description,
        success,
        error,
        progress: {
          completed: this.preloadStatus.completed,
          failed: this.preloadStatus.failed,
          total: this.preloadStatus.total,
          percentage: Math.round(
            ((this.preloadStatus.completed + this.preloadStatus.failed) / this.preloadStatus.total) * 100
          )
        }
      }
    });

    window.dispatchEvent(progressEvent);
  }

  // Force refresh all cached data
  async refreshAllData() {
    
    // Clear existing cache first
    await cacheService.forceInvalidateCache();
    
    // Restart preload
    return await this.startPreload();
  }

  // Preload user-specific data (called after login)
  async preloadUserData() {
    // Check token before preloading
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      return;
    }
    
    if (!authAPI.getProfile) {
      return;
    }

    try {
      
      // High priority user data
      const userEndpoints = [
        'getMyProduce',
        'getFarmManagement', 
        'getFarmerWallet',
        'getFarmerOrders'
      ];

      const promises = userEndpoints.map(async (method) => {
        // Check token again before each request
        const token = localStorage.getItem('authToken');
        if (!token) {
          return;
        }
        
        try {
          await authAPI[method]();
        } catch (error) {
          // Don't log 401 errors - they're expected when token is invalid
          if (error.response?.status !== 401) {
            console.warn(`[Preload] ❌ Failed to load ${method}:`, error.message);
          }
        }
      });

      await Promise.allSettled(promises);
      
    } catch (error) {
      // Silent fail
    }
  }

  // Get preload status
  getStatus() {
    return {
      ...this.preloadStatus,
      isPreloading: this.isPreloading
    };
  }

  // Check if critical data is available offline
  async checkOfflineAvailability() {
    const endpoints = this.getCriticalEndpoints();
    const availability = {};

    for (const endpoint of endpoints) {
      try {
        // Check if data exists in cache
        const cachedData = await cacheService.getCachedResponse(
          this.getUrlForMethod(endpoint.method),
          'GET'
        );
        availability[endpoint.method] = !!cachedData;
      } catch (error) {
        availability[endpoint.method] = false;
      }
    }

    return availability;
  }

  // Helper to get URL for API method (simplified mapping)
  getUrlForMethod(method) {
    const methodToUrl = {
      'getMyProduce': '/my_produce',
      'getProduce': '/auth/produce',
      'getFarmManagement': '/manage/farm',
      'getCategories': '/categories',
      'getFarmerWallet': '/farmer/wallet',
      'getFarmerOrders': '/farmer/orders',
      'getFarmerWithdrawals': '/farmer/withdrawals',
      'getConversations': '/conversations',
      'getProducts': '/products'
    };

    return methodToUrl[method] || '';
  }

  // Schedule periodic data refresh
  schedulePeriodicRefresh(intervalMinutes = 30) {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    this.refreshInterval = setInterval(async () => {
      // Check token before refreshing
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        // Stop refresh if no token
        this.stopPeriodicRefresh();
        return;
      }
      
      const isOnline = await networkUtils.isConnected();
      if (isOnline && !this.isPreloading) {
        await this.startPreload();
      }
    }, intervalMinutes * 60 * 1000);

  }

  // Stop periodic refresh
  stopPeriodicRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }
}

// Create singleton instance
const preloadService = new PreloadService();

export default preloadService;
